import { JsonProperty, JsonObject, Any } from 'json2typescript';
import { ModelObject } from '@hermes/api-model-core';
import { Asset } from '@hermes/api-model-asset';
import { Coordinates } from '@hermes/api-model-common';
import { Hreflang } from '@hermes/api-model-head';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * This class contain the stock of a product in a given shop
 */
let ShopStock = class ShopStock {
    constructor() {
        this.cegidId = undefined;
        this.available = undefined;
        this.limited = undefined;
        this.ereservationActivated = undefined;
    }
};
__decorate([
    JsonProperty("cegid_id", String, false),
    __metadata("design:type", String)
], ShopStock.prototype, "cegidId", undefined);
__decorate([
    JsonProperty("available", Boolean, false),
    __metadata("design:type", Boolean)
], ShopStock.prototype, "available", undefined);
__decorate([
    JsonProperty("limited", Boolean, false),
    __metadata("design:type", Boolean)
], ShopStock.prototype, "limited", undefined);
__decorate([
    JsonProperty("ereservation_activated", Boolean, false),
    __metadata("design:type", Boolean)
], ShopStock.prototype, "ereservationActivated", undefined);
ShopStock = __decorate([
    JsonObject("ShopStock")
], ShopStock);

/**
 * This class contain the stock of a product in a given store
 */
let StockProduct = class StockProduct extends ModelObject {
    constructor() {
        super(...arguments);
        /**
         * Contains SKU
         */
        this.itemCode = undefined;
        this.shops = [];
    }
    /**
     * @param value
     */
    addShop(value) {
        this.shops.push(value);
    }
};
__decorate([
    JsonProperty("itemCode", String, true),
    __metadata("design:type", String)
], StockProduct.prototype, "itemCode", undefined);
__decorate([
    JsonProperty("stores", [ShopStock]),
    __metadata("design:type", Array)
], StockProduct.prototype, "shops", undefined);
StockProduct = __decorate([
    JsonObject("StockProduct")
], StockProduct);

let StockLevelByStore = class StockLevelByStore extends ModelObject {
    constructor() {
        super(...arguments);
        this.inventoryDetailsByStore = [];
    }
};
__decorate([
    JsonProperty("inventoryDetailsByStore", [StockProduct]),
    __metadata("design:type", Array)
], StockLevelByStore.prototype, "inventoryDetailsByStore", undefined);
StockLevelByStore = __decorate([
    JsonObject("StockLevelByStore")
], StockLevelByStore);

/**
 * List of store data
 * @dynamic
 */
let ShopList = class ShopList extends ModelObject {
    constructor(source) {
        super();
        this.shops = undefined;
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("shops"),
    __metadata("design:type", Array)
], ShopList.prototype, "shops", undefined);
ShopList = __decorate([
    JsonObject("ShopList"),
    __metadata("design:paramtypes", [Object])
], ShopList);

/**
 * Data used to display a Store.
 * @dynamic
 */
let Shop = class Shop extends ModelObject {
    constructor(source) {
        super();
        this.shortTitle = undefined;
        this.image = undefined;
        this.streetAddress1 = undefined;
        this.streetAddress2 = undefined;
        this.streetAddress3 = undefined;
        this.city = undefined;
        this.area = undefined;
        this.postalCode = undefined;
        this.phoneNumber = undefined;
        this.phoneNumberLink = undefined;
        this.faxNumber = undefined;
        this.geoCoordinates = undefined;
        this.access = undefined;
        this.appointmentInStore = false;
        this.openingHours = undefined;
        this.additionalInformation = undefined;
        this.collectionH = undefined;
        this.baiduGeoCoordinates = undefined;
        this.zeus = undefined;
        this.email = undefined;
        this.sendMail = undefined;
        this.shopId = undefined;
        this.cegidId = undefined;
        this.cegidIdHcare = undefined;
        this.mercureId = undefined;
        this.gaddId = undefined;
        this.description = undefined;
        this.productLocator = undefined;
        this.country = undefined;
        this.continent = undefined;
        this.url = undefined;
        this.assets = undefined;
        this.hreflangs = undefined;
        this.countryCode = undefined;
        this.lang = undefined;
        this.streetNumber = undefined;
        this.additionalNumber = undefined;
        this.neighborhood = undefined;
        this.district = undefined;
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("short_title", String, true),
    __metadata("design:type", String)
], Shop.prototype, "shortTitle", undefined);
__decorate([
    JsonProperty("image", [String], true),
    __metadata("design:type", Array)
], Shop.prototype, "image", undefined);
__decorate([
    JsonProperty("street_address1", String, true),
    __metadata("design:type", String)
], Shop.prototype, "streetAddress1", undefined);
__decorate([
    JsonProperty("street_address2", String, true),
    __metadata("design:type", String)
], Shop.prototype, "streetAddress2", undefined);
__decorate([
    JsonProperty("street_address3", String, true),
    __metadata("design:type", String)
], Shop.prototype, "streetAddress3", undefined);
__decorate([
    JsonProperty("city", String, true),
    __metadata("design:type", String)
], Shop.prototype, "city", undefined);
__decorate([
    JsonProperty("area", String, true),
    __metadata("design:type", String)
], Shop.prototype, "area", undefined);
__decorate([
    JsonProperty("postal_code", String, true),
    __metadata("design:type", String)
], Shop.prototype, "postalCode", undefined);
__decorate([
    JsonProperty("store_phone_number", String, true),
    __metadata("design:type", String)
], Shop.prototype, "phoneNumber", undefined);
__decorate([
    JsonProperty("store_phone_number_link", String, true),
    __metadata("design:type", String)
], Shop.prototype, "phoneNumberLink", undefined);
__decorate([
    JsonProperty("store_fax_number", String, true),
    __metadata("design:type", String)
], Shop.prototype, "faxNumber", undefined);
__decorate([
    JsonProperty("geo_coordinates", Coordinates, true),
    __metadata("design:type", Coordinates)
], Shop.prototype, "geoCoordinates", undefined);
__decorate([
    JsonProperty("access", String, true),
    __metadata("design:type", String)
], Shop.prototype, "access", undefined);
__decorate([
    JsonProperty("appointment_in_store", Boolean, true),
    __metadata("design:type", Boolean)
], Shop.prototype, "appointmentInStore", undefined);
__decorate([
    JsonProperty("opening_hours", Any, true),
    __metadata("design:type", Object)
], Shop.prototype, "openingHours", undefined);
__decorate([
    JsonProperty("additional_information", String, true),
    __metadata("design:type", String)
], Shop.prototype, "additionalInformation", undefined);
__decorate([
    JsonProperty("collection_h", Boolean, true),
    __metadata("design:type", Boolean)
], Shop.prototype, "collectionH", undefined);
__decorate([
    JsonProperty("baidu_geo_coordinates", Coordinates, true),
    __metadata("design:type", Coordinates)
], Shop.prototype, "baiduGeoCoordinates", undefined);
__decorate([
    JsonProperty("zeus", Boolean, true),
    __metadata("design:type", Boolean)
], Shop.prototype, "zeus", undefined);
__decorate([
    JsonProperty("email", String, true),
    __metadata("design:type", String)
], Shop.prototype, "email", undefined);
__decorate([
    JsonProperty("send_mail", Boolean, true),
    __metadata("design:type", Boolean)
], Shop.prototype, "sendMail", undefined);
__decorate([
    JsonProperty("store_id", String, true),
    __metadata("design:type", String)
], Shop.prototype, "shopId", undefined);
__decorate([
    JsonProperty("cegid_id", String, true),
    __metadata("design:type", String)
], Shop.prototype, "cegidId", undefined);
__decorate([
    JsonProperty("cegid_id_hcare", String, true),
    __metadata("design:type", String)
], Shop.prototype, "cegidIdHcare", undefined);
__decorate([
    JsonProperty("mercure_id", String, true),
    __metadata("design:type", String)
], Shop.prototype, "mercureId", undefined);
__decorate([
    JsonProperty("gadd_id", String, true),
    __metadata("design:type", String)
], Shop.prototype, "gaddId", undefined);
__decorate([
    JsonProperty("description", String, true),
    __metadata("design:type", String)
], Shop.prototype, "description", undefined);
__decorate([
    JsonProperty("product_locator", Boolean, true),
    __metadata("design:type", Boolean)
], Shop.prototype, "productLocator", undefined);
__decorate([
    JsonProperty("country", String, true),
    __metadata("design:type", String)
], Shop.prototype, "country", undefined);
__decorate([
    JsonProperty("continent", String, true),
    __metadata("design:type", String)
], Shop.prototype, "continent", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], Shop.prototype, "url", undefined);
__decorate([
    JsonProperty("assets", [Asset], true),
    __metadata("design:type", Array)
], Shop.prototype, "assets", undefined);
__decorate([
    JsonProperty("hreflangs", [Hreflang], true),
    __metadata("design:type", Array)
], Shop.prototype, "hreflangs", undefined);
__decorate([
    JsonProperty("country_code", String, true),
    __metadata("design:type", String)
], Shop.prototype, "countryCode", undefined);
__decorate([
    JsonProperty("lang", String, true),
    __metadata("design:type", String)
], Shop.prototype, "lang", undefined);
__decorate([
    JsonProperty("street_number", String, true),
    __metadata("design:type", String)
], Shop.prototype, "streetNumber", undefined);
__decorate([
    JsonProperty("additional_number", String, true),
    __metadata("design:type", String)
], Shop.prototype, "additionalNumber", undefined);
__decorate([
    JsonProperty("neighborhood", String, true),
    __metadata("design:type", String)
], Shop.prototype, "neighborhood", undefined);
__decorate([
    JsonProperty("district", String, true),
    __metadata("design:type", String)
], Shop.prototype, "district", undefined);
Shop = __decorate([
    JsonObject("StoreData"),
    __metadata("design:paramtypes", [Object])
], Shop);

/**
 * List of store data
 * @dynamic
 */
let StoreDataList = class StoreDataList extends ModelObject {
    constructor(source) {
        super();
        this.stores = undefined;
        Object.assign(this, source);
    }
};
__decorate([
    JsonProperty("stores"),
    __metadata("design:type", Array)
], StoreDataList.prototype, "stores", undefined);
StoreDataList = __decorate([
    JsonObject("StoreDataList"),
    __metadata("design:paramtypes", [Object])
], StoreDataList);

export { Shop, ShopList, ShopStock, StockLevelByStore, StockProduct, StoreDataList };
